<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { writable, get } from "svelte/store";
    import { theme } from "$stores/themeStore";
    import { selectedStudy } from '$stores/selectionStore';
    import { userRole, isReadOnly } from '$stores/sessionStore';
    import { isFeatureEnabled } from '$stores/configStore';
    import { isValidEmail, parseEmailList, formatEmailList } from '$utils/emailValidation';
    // Using local API endpoints instead of direct API access
    const API_BASE_URL = '/api/proxy';

    const dispatch = createEventDispatcher();
    export let showForm = false;
    export let editSite = null;

    let statusOptions = [];
    let countryOptions = [];
    let timezoneOptions = [];

    // Add error states
    let siteIdError = '';
    let siteNameError = '';
    let countryError = '';
    let timezoneError = '';
    let emailsError = '';

    let lastCheckedSiteId = '';
    let isSubmitting = false;

    // Email management
    let emailList = [];
    let newEmail = '';
    let emailInputError = '';

    // Store original data for change detection
    let originalSiteData = null;
    let hasChanges = false;

    let siteData = writable({
        _id: null,
        siteId: "",
        siteName: "",
        description: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        countryGUID: "",
        primaryInvestigator: "",
        emails: "",
        phoneNumber: "",
        notes: "",
        active: true,
        studyGUID: $selectedStudy?._id || "",
        statusGUID: "075e70d8c67b11ecbac01636",
        externalId: "",
        timezoneGUID: "",
        enrollmentTarget: "",
        enrollmentStartDate: "",
        enrollmentEndDate: ""
    });

    onMount(async () => {
        try {
            // Fetch status options
            const statusResponse = await fetch(`${API_BASE_URL}/lookups/STAT`);
            const statusResult = await statusResponse.json();
            statusOptions = statusResult.data;

            // Fetch country options
            const countryResponse = await fetch(`${API_BASE_URL}/lookups/COUNTRY`);
            const countryResult = await countryResponse.json();
            countryOptions = countryResult.data;

            // Fetch timezone options
            const timezoneResponse = await fetch(`${API_BASE_URL}/lookups/TIMEZONE`);
            const timezoneResult = await timezoneResponse.json();
            timezoneOptions = timezoneResult.data;

        } catch (error) {
            console.error('Error fetching options:', error);
        }
    });

    $: if (editSite && showForm) {
        const formattedSite = { ...editSite };
        if (editSite.enrollmentStartDate) {
            const date = new Date(editSite.enrollmentStartDate);
            formattedSite.enrollmentStartDate = date.toLocaleDateString('en-CA');
        }
        if (editSite.enrollmentEndDate) {
            const date = new Date(editSite.enrollmentEndDate);
            formattedSite.enrollmentEndDate = date.toLocaleDateString('en-CA');
        }
        siteData.set(formattedSite);
        // Initialize email list from the emails field
        emailList = parseEmailList(editSite.emails || '');
        // Store original data for change detection
        originalSiteData = JSON.parse(JSON.stringify({...formattedSite, emails: editSite.emails || ''}));
        hasChanges = false;
    }

    $: if (showForm && !editSite) {
        resetSiteData();
        emailList = [];
        originalSiteData = null;
        hasChanges = false;
    }

    // Detect changes by comparing current data with original data
    $: if ($siteData) {
        if (originalSiteData) {
            // For editing existing sites, compare with original data
            const currentData = JSON.parse(JSON.stringify($siteData));
            const originalData = JSON.parse(JSON.stringify(originalSiteData));
            
            hasChanges = JSON.stringify(currentData) !== JSON.stringify(originalData);
        } else {
            // For new sites, check if any data has been entered
            const hasData = $siteData.siteId?.trim() || 
                           $siteData.siteName?.trim() || 
                           $siteData.countryGUID || 
                           $siteData.timezoneGUID ||
                           emailList.length > 0 ||
                           $siteData.address1?.trim() ||
                           $siteData.address2?.trim() ||
                           $siteData.city?.trim() ||
                           $siteData.state?.trim() ||
                           $siteData.postalCode?.trim() ||
                           $siteData.primaryInvestigator?.trim() ||
                           $siteData.phoneNumber?.trim() ||
                           $siteData.notes?.trim() ||
                           $siteData.externalId?.trim() ||
                           $siteData.enrollmentTarget ||
                           $siteData.enrollmentStartDate ||
                           $siteData.enrollmentEndDate;
            
            hasChanges = !!hasData;
        }
    }

    function resetSiteData() {
        siteData.set({
            _id: null,
            siteId: "",
            siteName: "",
            description: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
            countryGUID: "",
            primaryInvestigator: "",
            emails: "",
            phoneNumber: "",
            notes: "",
            active: true,
            studyGUID: $selectedStudy?._id || "",
            statusGUID: "075e70d8c67b11ecbac01636",
            externalId: "",
            timezoneGUID: "",
            enrollmentTarget: "",
            enrollmentStartDate: "",
            enrollmentEndDate: ""
        });
        
        // Reset email management
        emailList = [];
        newEmail = '';
        emailInputError = '';
        emailsError = '';
    }

    function close() {
        // Reset change detection when closing
        originalSiteData = null;
        hasChanges = false;
        dispatch("close");
    }

    function organizeTimezones(timezones) {
        const regions = {
            'United States': [],
            'Canada': [],
            'America (Other)': [],
            'Europe': [],
            'Asia': [],
            'Africa': [],
            'Pacific': [],
            'Australia': [],
            'Atlantic': [],
            'Indian': [],
            'Other': []
        };

        timezones.forEach(tz => {
            const [region, country, ...rest] = tz.code.split('/');
            
            // Special case for US territories in Pacific region
            if (tz.code === 'Pacific/Honolulu') {
                regions['United States'].push(tz);
                return;
            }
            
            if (region === 'America') {
                if (tz.code.startsWith('America/New_York') || 
                    tz.code.startsWith('America/Chicago') ||
                    tz.code.startsWith('America/Denver') ||
                    tz.code.startsWith('America/Los_Angeles') ||
                    tz.code.startsWith('America/Phoenix') ||
                    tz.code.startsWith('America/Anchorage') ||
                    tz.code.startsWith('America/Adak') ||
                    tz.code.startsWith('America/Boise') ||
                    tz.code.startsWith('America/Detroit') ||
                    tz.code.includes('Indiana') ||
                    tz.code.includes('Kentucky') ||
                    tz.code.includes('North_Dakota') ||
                    tz.code.includes('Michigan') ||
                    tz.code.startsWith('America/Juneau') ||
                    tz.code.startsWith('America/Sitka') ||
                    tz.code.startsWith('America/Metlakatla') ||
                    tz.code.startsWith('America/Yakutat') ||
                    tz.code.startsWith('America/Nome') ||
                    tz.code.startsWith('America/Menominee')) {
                    regions['United States'].push(tz);
                } else if (tz.code.startsWith('America/Vancouver') ||
                         tz.code.startsWith('America/Edmonton') ||
                         tz.code.startsWith('America/Winnipeg') ||
                         tz.code.startsWith('America/Toronto') ||
                         tz.code.startsWith('America/Halifax') ||
                         tz.code.startsWith('America/St_Johns')) {
                    regions['Canada'].push(tz);
                } else {
                    regions['America (Other)'].push(tz);
                }
            } else if (regions[region]) {
                regions[region].push(tz);
            } else {
                regions['Other'].push(tz);
            }
        });

        Object.keys(regions).forEach(region => {
            regions[region].sort((a, b) => {
                if (region === 'United States') {
                    const order = {
                        'America/New_York': 1,
                        'America/Detroit': 2,
                        'America/Indiana': 3,
                        'America/Kentucky': 4,
                        'America/Chicago': 5,
                        'America/Menominee': 6,
                        'America/North_Dakota': 7,
                        'America/Denver': 8,
                        'America/Boise': 9,
                        'America/Phoenix': 10,
                        'America/Los_Angeles': 11,
                        'America/Juneau': 12,
                        'America/Sitka': 13,
                        'America/Metlakatla': 14,
                        'America/Yakutat': 15,
                        'America/Anchorage': 16,
                        'America/Nome': 17,
                        'America/Adak': 18,
                        'Pacific/Honolulu': 19
                    };
                    const aBase = Object.keys(order).find(key => a.code.startsWith(key)) || a.code;
                    const bBase = Object.keys(order).find(key => b.code.startsWith(key)) || b.code;
                    const orderDiff = (order[aBase] || 99) - (order[bBase] || 99);
                    if (orderDiff !== 0) return orderDiff;
                    return a.code.localeCompare(b.code);
                }
                return a.code.localeCompare(b.code);
            });
        });

        return Object.fromEntries(
            Object.entries(regions).filter(([_, zones]) => zones.length > 0)
        );
    }

    function getTimezoneOffset(timezone) {
        try {
            const date = new Date();
            const options = { timeZone: timezone, timeZoneName: 'short' };
            const formatter = new Intl.DateTimeFormat('en-US', options);
            const parts = formatter.formatToParts(date);
            const timeZonePart = parts.find(part => part.type === 'timeZoneName');
            return timeZonePart ? ` (${timeZonePart.value})` : '';
        } catch (error) {
            console.error('Error getting timezone offset:', error);
            return '';
        }
    }

    async function isSiteIdUnique(siteId) {
        try {
            const excludeId = editSite?._id || '';
            const studyGUID = $selectedStudy?._id || '';
            const url = excludeId 
                ? `${API_BASE_URL}/sites/validate/siteId/${encodeURIComponent(siteId.trim())}?studyGUID=${studyGUID}&excludeId=${excludeId}`
                : `${API_BASE_URL}/sites/validate/siteId/${encodeURIComponent(siteId.trim())}?studyGUID=${studyGUID}`;
            
            const response = await fetch(url);
            const result = await response.json();

            if (response.ok) {
                return result.isUnique;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error checking site ID uniqueness:', error);
            return false;
        }
    }

    async function isSiteNameUnique(siteName) {
        try {
            const excludeId = editSite?._id || '';
            const studyGUID = $selectedStudy?._id || '';
            const url = excludeId 
                ? `${API_BASE_URL}/sites/validate/siteName/${encodeURIComponent(siteName.trim())}?studyGUID=${studyGUID}&excludeId=${excludeId}`
                : `${API_BASE_URL}/sites/validate/siteName/${encodeURIComponent(siteName.trim())}?studyGUID=${studyGUID}`;
            
            const response = await fetch(url);
            const result = await response.json();

            if (response.ok) {
                return result.isUnique;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error checking site name uniqueness:', error);
            return false;
        }
    }

    async function validateForm() {
        let isValid = true;

        // Reset errors at start of validation
        siteIdError = '';
        siteNameError = '';
        countryError = '';
        timezoneError = '';
        emailsError = '';

        // Validate Site ID
        if (!$siteData.siteId || !$siteData.siteId.trim()) {
            siteIdError = 'Site ID is required';
            isValid = false;
        } else {
            try {
                const isUnique = await isSiteIdUnique($siteData.siteId);
                console.log('Is site ID unique:', isUnique);
                if (!isUnique) {
                    siteIdError = 'This Site ID is already in use within this study';
                    isValid = false;
                }
            } catch (error) {
                console.error('Error in site ID validation:', error);
                siteIdError = 'Error validating Site ID';
                isValid = false;
            }
        }

        // Validate Site Name
        if (!$siteData.siteName || !$siteData.siteName.trim()) {
            siteNameError = 'Site Name is required';
            isValid = false;
        } else {
            try {
                const isUnique = await isSiteNameUnique($siteData.siteName);
                if (!isUnique) {
                    siteNameError = 'This Site Name is already in use within this study';
                    isValid = false;
                }
            } catch (error) {
                console.error('Error in site name validation:', error);
                siteNameError = 'Error validating Site Name';
                isValid = false;
            }
        }

        // Validate Country
        if (!$siteData.countryGUID) {
            countryError = 'Country is required';
            isValid = false;
        } else {
            countryError = '';
        }

        // Validate Timezone
        if (!$siteData.timezoneGUID) {
            timezoneError = 'Timezone is required';
            isValid = false;
        } else {
            timezoneError = '';
        }

        // Validate Emails (if any are provided)
        if (emailList.length > 0) {
            const invalidEmails = emailList.filter(email => !isValidEmail(email));
            if (invalidEmails.length > 0) {
                emailsError = `Invalid email(s): ${invalidEmails.join(', ')}`;
                isValid = false;
            }
        }

        return isValid;
    }

    async function handleSubmit() {
        if (isSubmitting) return;
        isSubmitting = true;
        try {
            // An address typed into the email box but never Added is intent,
            // not noise — fold it in rather than silently discarding it on
            // Save ("the email doesn't always save", Nik, SAB cutover day).
            // If it's invalid, addEmail() sets the error and we stop so the
            // user sees why instead of losing it.
            if (newEmail.trim()) {
                addEmail();
                if (emailInputError) {
                    return;
                }
            }

            if (!await validateForm()) {
                return;
            }
            
            const site = get(siteData);

            // Trim the data before submitting
            site.siteId = site.siteId.trim();
            site.siteName = site.siteName.trim();

            // Convert dates to ISO strings
            if (site.enrollmentStartDate) {
                const [year, month, day] = site.enrollmentStartDate.split('-');
                site.enrollmentStartDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
            }
            if (site.enrollmentEndDate) {
                const [year, month, day] = site.enrollmentEndDate.split('-');
                site.enrollmentEndDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
            }
            
            dispatch("submit", site);
            close();
        } finally {
            isSubmitting = false;
        }
    }

    // Email management functions
    function addEmail() {
        if (!newEmail.trim()) {
            emailInputError = 'Please enter an email address';
            return;
        }
        
        if (!isValidEmail(newEmail.trim())) {
            emailInputError = 'Please enter a valid email address';
            return;
        }
        
        const trimmedEmail = newEmail.trim();
        if (emailList.includes(trimmedEmail)) {
            emailInputError = 'This email is already in the list';
            return;
        }
        
        emailList = [...emailList, trimmedEmail];
        newEmail = '';
        emailInputError = '';
        
        // Update the siteData emails field
        siteData.update(data => ({
            ...data,
            emails: formatEmailList(emailList)
        }));
    }
    
    function removeEmail(index) {
        emailList = emailList.filter((_, i) => i !== index);
        
        // Update the siteData emails field
        siteData.update(data => ({
            ...data,
            emails: formatEmailList(emailList)
        }));
    }
    
    function handleEmailKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            addEmail();
        }
    }
    
    // Clear error when siteId changes but don't modify lastCheckedSiteId anymore
    $: if ($siteData.siteId) {
        siteIdError = '';
    }
    
    // Clear error when siteName changes
    $: if ($siteData.siteName) {
        siteNameError = '';
    }
    
    // Keep email list in sync with siteData.emails when it changes externally
    $: if ($siteData.emails !== formatEmailList(emailList)) {
        const parsedEmails = parseEmailList($siteData.emails);
        if (JSON.stringify(parsedEmails) !== JSON.stringify(emailList)) {
            emailList = parsedEmails;
        }
    }


</script>

{#if showForm}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
        <div 
            class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60 overflow-y-auto max-h-[90vh]"
            on:click|stopPropagation
        >
            <header class="flex justify-between items-start mb-4">
                <div class="flex items-center gap-2">
                    <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {editSite ? "Edit Site" : "New Site"}
                    </h2>
                    {#if editSite && hasChanges}
                        <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full dark:bg-yellow-900 dark:text-yellow-200">
                            Modified
                        </span>
                    {/if}
                </div>
                <button
                    on:click={close}
                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                >
                    &times;
                </button>
            </header>
            <form on:submit|preventDefault={handleSubmit} class="space-y-4">
                <div class="flex gap-3">
                    <div class="w-1/2">
                        <label
                            for="siteId"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Site ID *</label
                        >
                        <input
                            type="text"
                            id="siteId"
                            bind:value={$siteData.siteId}
                            on:input={() => {
                                siteIdError = '';
                                lastCheckedSiteId = '';
                            }}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {siteIdError ? 'border-red-500' : ''}"
                            required
                        />
                        {#if siteIdError}
                            <p class="text-red-500 text-xs mt-1">{siteIdError}</p>
                        {/if}
                    </div>
                    <div class="w-1/2">
                        <label
                            for="externalId"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >External ID</label
                        >
                        <input
                            type="text"
                            id="externalId"
                            bind:value={$siteData.externalId}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                    <div>
                        <label
                            for="status"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Status</label
                        >
                        <select
                            id="status"
                            bind:value={$siteData.statusGUID}
                            class="form-select h-8 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                            {#each statusOptions as option}
                                <option value={option.id}>{option.label}</option>
                            {/each}
                        </select>
                    </div>
                    <div>
                        <label
                            for="active"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Active</label
                        >
                        <select
                            id="active"
                            bind:value={$siteData.active}
                            class="form-select h-8 w-16 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                            <option value={true}>Yes</option>
                            <option value={false}>No</option>
                        </select>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-full">
                        <label
                            for="siteName"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Site Name *</label
                        >
                        <input
                            type="text"
                            id="siteName"
                            bind:value={$siteData.siteName}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {siteNameError ? 'border-red-500' : ''}"
                            required
                        />
                        {#if siteNameError}
                            <p class="text-red-500 text-xs mt-1">{siteNameError}</p>
                        {/if}
                    </div>
                </div>
                <div class="flex gap-3 pt-8">
                    <div class="w-1/2">
                        <label
                            for="address1"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Address 1</label
                        >
                        <input
                            type="text"
                            id="address1"
                            bind:value={$siteData.address1}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                    <div class="w-1/2">
                        <label
                            for="address2"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Address 2</label
                        >
                        <input
                            type="text"
                            id="address2"
                            bind:value={$siteData.address2}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-1/3">
                        <label
                            for="city"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >City</label
                        >
                        <input
                            type="text"
                            id="city"
                            bind:value={$siteData.city}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                    <div class="w-1/3">
                        <label
                            for="state"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >State</label
                        >
                        <input
                            type="text"
                            id="state"
                            bind:value={$siteData.state}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                    <div class="w-1/3">
                        <label
                            for="postalCode"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Postal Code</label
                        >
                        <input
                            type="text"
                            id="postalCode"
                            bind:value={$siteData.postalCode}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-1/2">
                        <label
                            for="country"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Country *</label
                        >
                        <select
                            id="country"
                            bind:value={$siteData.countryGUID}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {countryError ? 'border-red-500' : ''}"
                            required
                        >
                            <option value="">Select Country</option>
                            {#each countryOptions as option}
                                <option value={option.id}>{option.label}</option>
                            {/each}
                        </select>
                        {#if countryError}
                            <p class="text-red-500 text-xs mt-1">{countryError}</p>
                        {/if}
                    </div>
                    <div class="w-1/2">
                        <label
                            for="timezone"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Timezone *</label
                        >
                        <select
                            id="timezone"
                            bind:value={$siteData.timezoneGUID}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {timezoneError ? 'border-red-500' : ''}"
                            required
                        >
                            <option value="">Select Timezone</option>
                            {#each Object.entries(organizeTimezones(timezoneOptions)) as [region, zones]}
                                {#if zones.length > 0}
                                    <optgroup label={region}>
                                        {#each zones as timezone}
                                            <option value={timezone.id}>
                                                {timezone.code.split('/').slice(1).join('/')}{getTimezoneOffset(timezone.code)}
                                            </option>
                                        {/each}
                                    </optgroup>
                                {/if}
                            {/each}
                        </select>
                        {#if timezoneError}
                            <p class="text-red-500 text-xs mt-1">{timezoneError}</p>
                        {/if}
                    </div>
                </div>
                {#if isFeatureEnabled('performanceMetrics')}
                    <div class="flex gap-3 pt-8">
                        <div class="w-1/3">
                            <label
                                for="enrollmentTarget"
                                class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                                >Enrollment Target</label
                            >
                            <input
                                type="number"
                                id="enrollmentTarget"
                                bind:value={$siteData.enrollmentTarget}
                                min="0"
                                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder="0"
                            />
                        </div>
                        <div class="w-1/3">
                            <label
                                for="enrollmentStartDate"
                                class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                                >Enrollment Start Date</label
                            >
                            <input
                                type="date"
                                id="enrollmentStartDate"
                                bind:value={$siteData.enrollmentStartDate}
                                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            />
                        </div>
                        <div class="w-1/3">
                            <label
                                for="enrollmentEndDate"
                                class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                                >Enrollment End Date</label
                            >
                            <input
                                type="date"
                                id="enrollmentEndDate"
                                bind:value={$siteData.enrollmentEndDate}
                                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            />
                        </div>
                    </div>
                {/if}
                <div class="flex gap-3 pt-8">
                    <div class="w-1/3">
                        <label
                            for="primaryInvestigator"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Primary Investigator</label
                        >
                        <input
                            type="text"
                            id="primaryInvestigator"
                            bind:value={$siteData.primaryInvestigator}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                        <label
                            for="phoneNumber"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200 mt-3"
                            >Phone Number</label
                        >
                        <input
                            type="text"
                            id="phoneNumber"
                            bind:value={$siteData.phoneNumber}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                    </div>
                    <div class="w-2/3">
                        <label
                            for="emails"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Emails</label
                        >
                        <div class="space-y-2">
                            <!-- Email list display -->
                            {#if emailList.length > 0}
                                <div class="max-h-32 overflow-y-auto border border-gray-300/50 dark:border-gray-500/50 rounded p-1 bg-gray-50 dark:bg-gray-800">
                                    {#each emailList as email, index}
                                        <div class="flex items-center justify-between text-xs py-1 px-2 bg-white dark:bg-gray-700 rounded mb-1">
                                            <span class="text-gray-700 dark:text-gray-300">{email}</span>
                                            <button
                                                type="button"
                                                on:click={() => removeEmail(index)}
                                                class="text-red-500 hover:text-red-700 ml-2"
                                                title="Remove email"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    {/each}
                                </div>
                            {/if}
                            
                            <!-- Add new email input -->
                            <div class="flex gap-1">
                                <input
                                    type="email"
                                    id="newEmail"
                                    bind:value={newEmail}
                                    on:keydown={handleEmailKeydown}
                                    placeholder="Add email address"
                                    class="flex-1 border p-1 h-8 rounded text-xs dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {emailInputError ? 'border-red-500' : ''}"
                                />
                                <button
                                    type="button"
                                    on:click={addEmail}
                                    class="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                                >
                                    Add
                                </button>
                            </div>
                            
                            {#if emailInputError}
                                <p class="text-red-500 text-xs">{emailInputError}</p>
                            {/if}
                            
                            {#if emailsError}
                                <p class="text-red-500 text-xs">{emailsError}</p>
                            {/if}
                        </div>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-full">
                        <label
                            for="notes"
                            class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                            >Notes</label
                        >
                        <textarea
                            id="notes"
                            bind:value={$siteData.notes}
                            class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            rows="3"
                        ></textarea>
                    </div>
                </div>
                <div class="flex justify-end space-x-2 pt-10">
                    <button
                        type="button"
                        on:click={close}
                        class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
                        >Cancel</button
                    >
                    <button
                        type="submit"
                        class="py-1 px-2 rounded {isReadOnly() || !hasChanges ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-700'}"
                        disabled={isReadOnly() || !hasChanges}
                    >
                        {hasChanges ? 'Save' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
